// Article types + content data.
// Articles support inline product mentions so any protocol or research note can
// surface its relevant peptides directly in the body or as a sidebar.

export type ArticleCategory =
  | 'Protocols'
  | 'Science'
  | 'Research notes'
  | 'Guides'

export type ArticleGoal =
  | 'Recovery'
  | 'Weight management'
  | 'Performance'
  | 'Skin & Hair'
  | 'Longevity'
  | 'Sleep'

export type ArticleBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; level?: 2 | 3; text: string }
  | { type: 'list'; items: string[]; ordered?: boolean }
  | { type: 'quote'; text: string; attribution?: string }
  | { type: 'callout'; tone?: 'info' | 'warning'; text: string }
  | { type: 'productMention'; productId: string; note?: string }

export type Article = {
  id: string
  slug: string
  title: string
  excerpt: string
  category: ArticleCategory
  readMinutes: number
  publishedAt: string // ISO date
  author: string
  authorRole: string
  goals: ArticleGoal[]
  content: ArticleBlock[]
  relatedProductIds: string[]
}

export const ARTICLES: Article[] = [
  {
    id: 'bpc-tb500-stack',
    slug: 'bpc-157-tb-500-recovery-protocol',
    title: 'The BPC-157 + TB-500 recovery stack, by the numbers',
    excerpt:
      'A practical look at why researchers keep pairing these two — what the in-vitro data actually shows, and how to read it.',
    category: 'Protocols',
    readMinutes: 6,
    publishedAt: '2026-04-12',
    author: 'Dr. Priya Anand',
    authorRole: 'Research lead',
    goals: ['Recovery'],
    content: [
      {
        type: 'paragraph',
        text: 'The BPC-157 + TB-500 pairing has become a default in soft-tissue recovery research. Both peptides have been investigated for tendon, ligament, and gut healing — but they act on different parts of the cascade, which is why researchers keep stacking them.',
      },
      {
        type: 'heading',
        text: 'What each one is doing',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'BPC-157 is the cytoprotective fragment — a 15 amino-acid peptide derived from a gastric juice protein. In rodent models it consistently shows angiogenic effects: better capillary growth around the injury site, faster fibroblast migration, downregulation of inflammatory mediators.',
      },
      {
        type: 'paragraph',
        text: 'TB-500 is the synthetic version of thymosin beta-4 — a peptide naturally present in higher concentrations at injury sites. Its primary role appears to be actin sequestration, which promotes cell migration. Different mechanism, similar downstream outcome.',
      },
      {
        type: 'productMention',
        productId: 'bpc-157',
        note: 'The standard healing fragment we keep in stock. ≥99.5% HPLC per batch.',
      },
      {
        type: 'productMention',
        productId: 'tb-500',
        note: 'Reconstitutes cleanly with BAC water. Pair with BPC-157 for stack protocols.',
      },
      {
        type: 'heading',
        text: 'Why they stack',
        level: 2,
      },
      {
        type: 'list',
        items: [
          'BPC-157 drives vascular response — TB-500 drives cellular migration.',
          'In published rodent work, the combination shows additive (not antagonistic) effects on tendon healing markers.',
          'Both have favourable in-vitro stability profiles when reconstituted with bacteriostatic water and refrigerated.',
        ],
      },
      {
        type: 'callout',
        tone: 'warning',
        text: 'All products on peptides.ml are sold for in-vitro laboratory research use only. The above summarises published literature; it is not protocol guidance for human use.',
      },
    ],
    relatedProductIds: ['bpc-157', 'tb-500'],
  },
  {
    id: 'glp1-purity-guide',
    slug: 'reading-a-glp-1-coa',
    title: 'How to read a GLP-1 certificate of analysis',
    excerpt:
      'HPLC purity is the headline number — but the mass-spec identity confirmation is the one that matters. A walkthrough.',
    category: 'Guides',
    readMinutes: 8,
    publishedAt: '2026-03-28',
    author: 'Dr. James Okonkwo',
    authorRole: 'Lab director',
    goals: ['Weight management'],
    content: [
      {
        type: 'paragraph',
        text: 'Every vial we ship comes with a Certificate of Analysis — a one-page document that summarises the third-party testing on that exact batch. For GLP-1 agonists in particular, knowing what each line means is the difference between a credible supplier and a marketing one.',
      },
      {
        type: 'heading',
        text: 'The four numbers that matter',
        level: 2,
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'HPLC purity (target ≥99%): how much of the vial is the intended peptide vs other peaks.',
          'Mass spectrometry identity (PASS / FAIL): confirms the molecular weight matches the intended sequence.',
          'Endotoxin level (target <0.5 EU/mg): bacterial endotoxin presence.',
          'Residual solvents (target trace): leftover synthesis solvents like TFA.',
        ],
      },
      {
        type: 'productMention',
        productId: 'semaglutide',
        note: 'Every batch ships with a COA. Latest batch tested by Eurofins UK.',
      },
      {
        type: 'paragraph',
        text: 'A COA without mass-spec is half a COA. HPLC tells you what fraction of the vial is "a peak" — but if the synthesis went sideways, that peak might not actually be the molecule you ordered. Mass spec is the only check that confirms identity.',
      },
      {
        type: 'quote',
        text: "When suppliers refuse to share mass-spec data, that's the answer to whether they have it.",
        attribution: 'Standard buyer rule',
      },
    ],
    relatedProductIds: ['semaglutide', 'tirzepatide'],
  },
  {
    id: 'cold-chain',
    slug: 'why-cold-chain-shipping-matters',
    title: "Cold-chain isn't a marketing word. Here's what it actually does.",
    excerpt:
      'Lyophilised peptides are stable at room temperature for short windows — but every degree-hour of excess heat eats into the shelf life.',
    category: 'Science',
    readMinutes: 4,
    publishedAt: '2026-03-14',
    author: 'Dr. Priya Anand',
    authorRole: 'Research lead',
    goals: ['Recovery', 'Longevity'],
    content: [
      {
        type: 'paragraph',
        text: 'Most lyophilised research peptides are nominally stable at 25°C for several weeks. So why bother with cold-chain at all? Two reasons: the "stable" range narrows fast under summer transport conditions, and shelf life is cumulative.',
      },
      {
        type: 'heading',
        text: 'The cumulative degradation problem',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Peptide hydrolysis follows roughly Arrhenius kinetics — every 10°C above storage spec roughly doubles the degradation rate. A vial that sits in a 35°C delivery van for 48 hours is consuming weeks of refrigerated shelf life.',
      },
      {
        type: 'callout',
        tone: 'info',
        text: 'We ship in insulated cold packs that maintain <8°C for up to 72 hours, with same-day UK dispatch before 2pm. Every shipment is tracked.',
      },
    ],
    relatedProductIds: ['bpc-157', 'tb-500', 'epitalon'],
  },
  {
    id: 'storage-handling',
    slug: 'storing-reconstituted-peptides',
    title: 'Storing reconstituted peptides: the 28-day rule',
    excerpt:
      'Why we tell researchers to use reconstituted vials within 28 days, and what changes if you go past it.',
    category: 'Research notes',
    readMinutes: 5,
    publishedAt: '2026-02-21',
    author: 'Dr. James Okonkwo',
    authorRole: 'Lab director',
    goals: ['Recovery', 'Performance'],
    content: [
      {
        type: 'paragraph',
        text: "Lyophilised peptides are remarkably stable. Once reconstituted with bacteriostatic water, however, the clock starts. The 28-day rule isn't arbitrary — it's where the published stability data tends to fall off for most common peptides stored at 2–8°C with benzyl alcohol as the preservative.",
      },
      {
        type: 'list',
        items: [
          'Keep reconstituted vials refrigerated at 2–8°C, never frozen (freeze-thaw cycles aggregate the peptide).',
          'Protect from light — amber or wrapped vials are best.',
          'Use within 28 days for protocols requiring intact concentration.',
        ],
      },
      {
        type: 'productMention',
        productId: 'cjc-1295',
        note: 'CJC-1295 is particularly sensitive to repeated temperature cycling — keep in one fridge zone.',
      },
    ],
    relatedProductIds: ['cjc-1295', 'ipamorelin', 'tesamorelin'],
  },
  {
    id: 'ghk-cu-cosmetic',
    slug: 'ghk-cu-skin-research',
    title: 'GHK-Cu: the copper peptide research keeps returning to',
    excerpt:
      'Forty years of in-vitro dermal research, and one of the few peptides with strong cosmetic literature behind it.',
    category: 'Science',
    readMinutes: 7,
    publishedAt: '2026-02-08',
    author: 'Dr. Priya Anand',
    authorRole: 'Research lead',
    goals: ['Skin & Hair'],
    content: [
      {
        type: 'paragraph',
        text: 'GHK-Cu is a tripeptide-copper complex first isolated from human plasma in 1973. Decades later it remains one of the most-cited peptides in dermal research — and one of the few with serious cosmetic literature underneath it.',
      },
      {
        type: 'productMention',
        productId: 'ghk-cu',
        note: 'Our copper-blue tinted vial — distinct from the others on shelf.',
      },
      {
        type: 'heading',
        text: 'What the research actually says',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'In-vitro work shows GHK-Cu modulates fibroblast activity, supports collagen synthesis pathways, and acts as a copper chaperone. The cosmetic industry has run with this — and unlike many "anti-aging" claims, this one has real biology beneath it.',
      },
    ],
    relatedProductIds: ['ghk-cu'],
  },
  {
    id: 'pricing-transparency',
    slug: 'why-our-prices',
    title: 'Why our prices are what they are',
    excerpt:
      "A breakdown of where peptide pricing actually comes from — synthesis, testing, cold-chain, and the things suppliers don't talk about.",
    category: 'Research notes',
    readMinutes: 5,
    publishedAt: '2026-01-30',
    author: 'Dr. James Okonkwo',
    authorRole: 'Lab director',
    goals: [],
    content: [
      {
        type: 'paragraph',
        text: 'Researchers ask us all the time why two suppliers can list "the same" peptide at very different prices. The honest answer: they\'re often not the same product — they\'re different synthesis grades, with different testing, in different supply chains.',
      },
      {
        type: 'list',
        items: [
          'Synthesis grade: pharmaceutical-grade SPPS costs 3–5× research-grade.',
          'Testing: a full COA (HPLC + mass spec + endotoxin) adds ~£8–15 per batch.',
          'Cold-chain: insulated shipping with tracked tail-end delivery is ~3× standard post.',
          'Insurance: damaged or compromised shipments replaced free of charge — we eat that cost.',
        ],
      },
      {
        type: 'paragraph',
        text: 'When a competitor is 40% cheaper, one of those four lines is missing. Worth checking which.',
      },
    ],
    relatedProductIds: [],
  },
]

export function getArticleBySlug(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug)
}

export function getRelatedArticles(article: Article, count = 2): Article[] {
  return ARTICLES.filter((a) => a.id !== article.id)
    .sort((a, b) => {
      const sharedGoalsA = a.goals.filter((g) =>
        article.goals.includes(g),
      ).length
      const sharedGoalsB = b.goals.filter((g) =>
        article.goals.includes(g),
      ).length
      const sameCategory = (x: Article) =>
        x.category === article.category ? 1 : 0
      return sharedGoalsB + sameCategory(b) - (sharedGoalsA + sameCategory(a))
    })
    .slice(0, count)
}
